import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert, Button, Card, CardHeader, ConfirmDialog, mutedTextClass } from '@/components/layout/ui'
import { projectsApi } from '@/lib/api'
import { cn } from '@/lib/cn'
import { messageFor } from '@/lib/errors'
import { queryKeys } from '@/lib/queryClient'
import { useWorkspace } from '@/lib/workspace'
import type { Project } from '@/types/api'

/**
 * Deleting a project.
 *
 * The endpoint has always existed; there was simply no way to reach it, so a
 * project created by mistake stayed forever.
 *
 * The description below is the important part, and it is deliberately specific
 * rather than reassuring. Deleting a project is NOT a soft delete: the server
 * moves its issues to the default project, and deletes its sprints and its
 * notes outright. Someone who reads "this cannot be undone" assumes they lose
 * the project; they need to know they also lose the sprints.
 *
 * The name has to be typed. For an action that destroys sprints and notes,
 * that is the difference between a mis-click and an intent.
 */
export function DangerZone({ project, canEdit }: { project: Project; canEdit: boolean }) {
  const workspace = useWorkspace()
  const navigate = useNavigate()
  const client = useQueryClient()
  const [open, setOpen] = useState(false)

  const remove = useMutation({
    mutationFn: () => projectsApi.remove(project.id),
    onSuccess: async () => {
      setOpen(false)
      await client.invalidateQueries({ queryKey: queryKeys.projects })
      await client.invalidateQueries({ queryKey: queryKeys.projectList(workspace.slug) })
      // Leave first: this screen is a view of something that no longer exists.
      navigate(workspace.path(), { replace: true })
    },
  })

  // The server refuses this and says so, but a button that can only ever fail
  // is worse than no button — the reason belongs next to it, before the click.
  const isDefault = project.is_default === true

  return (
    <Card className="border-red-200 dark:border-red-900">
      <CardHeader
        title="Delete this project"
        description="Its issues move to the default project. Its sprints and notes are deleted."
      />
      <div className="space-y-3 p-5">
        <p className={cn('text-sm', mutedTextClass)}>
          {isDefault
            ? 'The default project cannot be deleted — it is where issues from other deleted projects go. Make another project the default first.'
            : 'This cannot be undone. The issues are kept and moved to the default project, but the sprints and notes belonging to this project are removed with it.'}
        </p>
        {remove.isError && <Alert tone="danger">{messageFor(remove.error)}</Alert>}
        <Button
          variant="danger"
          disabled={!canEdit || isDefault || remove.isPending}
          onClick={() => setOpen(true)}
        >
          Delete project
        </Button>
      </div>

      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={() => remove.mutate()}
        title={`Delete ${project.name}?`}
        description={
          <>
            Its issues move to the default project. Its <strong>sprints and notes are deleted</strong>{' '}
            and cannot be recovered.
          </>
        }
        // Typing the name is what separates a mis-click from an intent.
        confirmWord={project.name}
        confirmLabel="Delete project"
        loading={remove.isPending}
        error={remove.isError ? messageFor(remove.error) : undefined}
      />
    </Card>
  )
}
